import {
  Box,
  Button,
  Divider,
  HStack,
  Heading,
  IconButton,
  Stack,
  VStack
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import useKeyControl from "@pathwright/utils/src/keyboard/useKeyControl"
import ResourceBackground from "@pathwright/ux/src/components/background/ResourceBackground"
import RadioPills, {
  RadioPillOption
} from "@pathwright/ux/src/components/buttons/RadioPills"
import Link, { LinkTo } from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { COVER_PAGE_PADDING } from "@pathwright/web/src/modules/cover-pages/constants"
import { AnimatePresence, motion } from "framer-motion"
import { Fragment, useMemo } from "react"
import useIntercomAugmentationToolbar from "../intercom/augmentation/useIntercomAugmentationToolbar"
import Share from "../share/Share"
import { CoverPageWrapper } from "./CoverPage"
import {
  IMMERSIVE_HEADER_HEIGHT,
  MobileCloseButton,
  ResourceImageCollapsed
} from "./CoverPageHeader"
import { useCoverPageContext } from "./CoverPageProvider"
import { ANIMATION_DURATION, EASE_BEZIER } from "./animations"
import { ImmersiveRouteConfig, immersiveRoutes } from "./immersiveRouteConfig"
import { CoverPageContainerProps, ImmersiveRoute } from "./types"
import { getNextAndPrevItems, useGetPathUrl } from "./utils"
const MotionBox = motion(Box)
const MotionHeading = motion(Heading)

export const ImmersiveTriggerButton = ({
  route
}: {
  route: ImmersiveRoute
}) => {
  const { onOpenImmersive } = useCoverPageContext()

  if (!onOpenImmersive || !route) return null

  return (
    <Button
      leftIcon={<Pathicon icon="cards-fill" />}
      onClick={() => onOpenImmersive(route)}
      variant="blurredLightGray"
      py={1}
      px={2}
    >
      Open
    </Button>
  )
}

export const ImmersiveSliderArrow = ({
  direction,
  onClick
}: {
  direction: "prev" | "next"
  onClick: () => void
}) => {
  const { isMobile } = useCoverPageContext()

  return (
    <VStack
      className="ImmersiveSliderArrow"
      h="100%"
      pos="absolute"
      top="0"
      bottom="0"
      align="center"
      justify="center"
      zIndex={1}
      left={direction === "prev" ? { base: "2px", sm: 0 } : undefined}
      right={direction === "next" ? { base: "2px", sm: 0 } : undefined}
    >
      <IconButton
        aria-label={direction}
        variant="blurredLightGray"
        display="flex"
        alignItems="center"
        justifyContent="center"
        // for some reason, the chevrons appear off centered
        pr={direction === "prev" ? "3px" : undefined}
        pl={direction === "next" ? "3px" : undefined}
        h={{ base: "32px", sm: "40px" }}
        w={{ base: "32px", sm: "40px" }}
        minW={{ base: "32px", sm: "40px" }}
        minH={{ base: "32px", sm: "40px" }}
        onClick={onClick}
        isRound
        icon={
          <Pathicon
            icon={
              `chevron-${direction === "prev" ? "left" : "right"}` as IconName
            }
            size={isMobile ? "1.4em" : "2em"}
          />
        }
      >
        {direction}
      </IconButton>
    </VStack>
  )
}

const ImmersiveNavigationArrows = () => {
  const coverPageContext = useCoverPageContext()
  const { onOpenImmersive, immersiveRoute } = coverPageContext

  const activeRoute = immersiveRoutes.find(
    (rc) => rc.key === immersiveRoute?.name
  )

  // All active routes will include:
  // Static routes, when data exists for the route (ex: overview, trailer, contributors, offers)
  // Dynamic routes (single routes for contributors, offers, previews)
  const getActiveImmersiveRoutes = () =>
    immersiveRoutes.reduce<ImmersiveRoute[]>(
      (acc: ImmersiveRoute[], route: ImmersiveRouteConfig) => [
        ...acc,
        ...route.getNavigableImmersiveRoutes(coverPageContext)
      ],
      []
    )

  const navigableRoutes = getActiveImmersiveRoutes()

  const nextAndPrev = getNextAndPrevItems<ImmersiveRoute>(
    navigableRoutes,
    immersiveRoute,
    {
      circular: false,
      getKey: (item) => item.id || item.name
    }
  )

  const handlePrevClick = () => {
    if (nextAndPrev?.prevItem) {
      onOpenImmersive?.(nextAndPrev.prevItem)
    }
  }

  const handleNextClick = () => {
    if (nextAndPrev?.nextItem) {
      onOpenImmersive?.(nextAndPrev.nextItem)
    }
  }

  useKeyControl("ArrowLeft", handlePrevClick)
  useKeyControl("ArrowRight", handleNextClick)

  if (!activeRoute || !immersiveRoute) return null

  return (
    <Box
      className="ImmersiveNavigationArrows"
      pos="absolute"
      top="50%"
      transform="translateY(-50%)"
      left="0"
      right="0"
      zIndex={1}
      px={{ base: 0, sm: 2 }}
    >
      <HStack
        justify="space-between"
        align="center"
        maxW="1100px"
        mx="auto"
        position="relative"
      >
        {nextAndPrev?.prevItem && (
          <ImmersiveSliderArrow direction="prev" onClick={handlePrevClick} />
        )}
        {nextAndPrev?.nextItem && (
          <ImmersiveSliderArrow direction="next" onClick={handleNextClick} />
        )}
      </HStack>
    </Box>
  )
}

const mobileBarButtonSize = { base: "36px", sm: "40px", md: "auto" }

// On mobile, we hide the augmentation toolbar
// and show the toolbar options in the navigation bar
const ImmersiveNavToolbar = () => {
  const resource = useCoverPageContext((v) => v.resource)

  const pathUrl = useGetPathUrl()
  const intercomOption = useIntercomAugmentationToolbar()

  const toolbarButtonStyles = {
    bgColor: "transparent",
    _hover: {
      bgColor: "transparent"
    },
    minW: "0",
    cursor: "pointer",
    px: 2,
    h: mobileBarButtonSize,
    w: mobileBarButtonSize
  }

  return (
    <>
      <Divider
        orientation="vertical"
        borderColor="whiteAlpha.900"
        h="100%"
        minH="20px"
        mx={1}
      />
      <HStack align="center" gap={0} pl={0} pr={0.5}>
        <Share
          context={{
            type: "cohort",
            typeId: resource?.curriculum_group?.id
          }}
          hideTabs
          openWithKeys
          onToggleIsOpen={null}
          placement={null}
          triggerLabel={null}
          portal={false}
          displayType="modal"
        >
          {({ renderTrigger }) =>
            renderTrigger(({ onToggle }: { onToggle: () => void }) => (
              <IconButton
                aria-label="Share"
                onClick={onToggle}
                display="flex"
                alignItems="center"
                justifyContent="center"
                icon={<Pathicon icon="share-fill" color="white" />}
                {...toolbarButtonStyles}
              />
            ))
          }
        </Share>
        {pathUrl && (
          <Link
            aria-label="Open Path"
            as={IconButton}
            to={pathUrl as LinkTo}
            icon={<Pathicon icon="path" color="white" />}
            {...toolbarButtonStyles}
          />
        )}
        {intercomOption && (
          <IconButton
            aria-label="Get Help"
            onClick={intercomOption.onClick}
            icon={<Pathicon icon={intercomOption.icon} color="white" />}
            {...toolbarButtonStyles}
          />
        )}
      </HStack>
    </>
  )
}

// Shows a navigation bar at the bottom of the immersive content
// with links to the different immersive routes
// Not all routes have a link (see displayAsNavLabel);
// some routes without a link, when active
//  will highlight a different (parent) route (ex: offer -> offers)
const ImmersiveNavigationBar = () => {
  const coverPageContext = useCoverPageContext()
  const { onOpenImmersive, isMobile, immersiveRoute, immersiveActive } =
    coverPageContext

  const { navItems, activeItemKey, options } = useMemo(() => {
    // 1) Get routes visible in the nav
    const visible = immersiveRoutes.filter((r) => {
      const show =
        r.displayAsNavLabel === true ||
        (typeof r.displayAsNavLabel === "function" &&
          r.displayAsNavLabel(coverPageContext))
      const renderable = r.shouldRender
        ? r.shouldRender(coverPageContext)
        : true
      return show && renderable
    })

    // 2) Group by navGroup (fallback to key)
    const groupMap = new Map<string, (typeof immersiveRoutes)[number]>()
    for (const r of visible) {
      const g = r.navGroup ?? r.key
      if (!groupMap.has(g)) groupMap.set(g, r)
    }
    const navItems = Array.from(groupMap.entries()).map(
      ([groupKey, config]) => ({
        groupKey,
        config
      })
    )

    // 3) Determine active group (route's navGroup or its own key)
    const activeRoute = immersiveRoutes.find(
      (rc) => rc.key === immersiveRoute?.name
    )
    const activeItemKey = activeRoute?.navGroup ?? activeRoute?.key

    // 4) Options for RadioPills / UI
    let options: RadioPillOption[] = navItems.map(({ config, groupKey }) => ({
      label: isMobile ? (
        config.navIcon ? (
          <Pathicon icon={config.navIcon} />
        ) : null
      ) : (
        config.navLabel ?? groupKey
      ),
      value: groupKey
    }))

    return { navItems, activeItemKey, options }
  }, [coverPageContext, isMobile, immersiveRoute])

  const handleChangeNavRoute = (index: number) => {
    const { groupKey, config } = navItems[index] || {}
    if (!config) return

    // If this group is a collection (has navigable items), and we're already in it, do nothing
    const items = config.getNavigableImmersiveRoutes?.(coverPageContext) || []
    const isCollection = items.length > 0

    const currentRoute = immersiveRoute
    const currentGroupKey =
      immersiveRoutes.find((rc) => rc.key === currentRoute?.name)?.navGroup ??
      currentRoute?.name

    if (isCollection) {
      if (currentGroupKey === groupKey) {
        // already in this collection; do nothing
        return
      }
      const first = items[0]
      if (first) {
        onOpenImmersive?.(first)
      }
      return
    }

    // Static routes
    const route = config.getImmersiveRoute?.(null)
    if (route) onOpenImmersive?.(route)
  }

  return (
    <HStack
      className="ImmersiveNavigationBar"
      justify="center"
      w="full"
      pt={4}
      pb={{ base: "20px", md: 10 }}
      sx={{
        ".RadioPills-highlight-offers": {
          backgroundColor: "transparent"
        },
        ".RadioPills-radio:not(.RadioPills-divider) .RadioPills-radio-visual": {
          paddingX: isMobile ? "var(--chakra-space-2)" : "var(--chakra-space-4)"
        },
        ".RadioPills-radio-offers .RadioPills-radio-visual": {
          backgroundColor: "whiteAlpha.900",
          color: "var(--chakra-colors-brand-500)",
          borderRadius: "25px",
          _hover: {
            backgroundColor: "white",
            color: "var(--chakra-colors-brand-500)"
          }
        },
        ".RadioPills-radio-offers.radio-pill-active .RadioPills-radio-visual": {
          backgroundColor: "var(--chakra-colors-brand-300)",
          color: "white",
          _hover: {
            backgroundColor: "var(--chakra-colors-brand-500)",
            color: "white"
          }
        },
        // Hide background because we'll merge Radio pills with toolbar options on mobile
        ".RadioPills-background": {
          display: "none"
        },
        ".RadioPills-radio-visual": {
          h: mobileBarButtonSize,
          w: mobileBarButtonSize
        }
      }}
    >
      <HStack
        className="ImmersiveNavigationBarContent"
        borderRadius="full"
        bgColor="rgba(153, 153, 153, 0.5)"
        gap={0}
        align="center"
        justify="center"
      >
        <RadioPills
          options={options}
          activeIndex={options.findIndex((r) => r.value === activeItemKey)}
          onChange={handleChangeNavRoute}
        />
        {isMobile && immersiveActive && <ImmersiveNavToolbar />}
      </HStack>
    </HStack>
  )
}

const ImmersiveNavigation = () => {
  return (
    <>
      <ImmersiveNavigationArrows />
      <ImmersiveNavigationBar />
    </>
  )
}

export type ImmersiveHeaderProps = {
  className?: string
  title?: React.ReactNode
  showShare?: boolean
  showClose?: boolean
}

export const ImmersiveHeader = ({ className, title }: ImmersiveHeaderProps) => {
  const resource = useCoverPageContext((v) => v.resource)
  const onClose = useCoverPageContext((v) => v.onClose)

  const { name } = resource || {}

  const headerClassName = ["ImmersiveHeader", className]
    .filter(Boolean)
    .join(" ")

  const handleClickImage = () => {
    onClose?.()
  }

  return (
    <HStack
      as="header"
      className={headerClassName}
      width="100%"
      maxW={{ base: "100%" }}
      m="0 auto"
      height={{ base: "70px", md: `${IMMERSIVE_HEADER_HEIGHT}px` }}
      align="center"
      gap={{ base: 2, md: 5 }}
      pt={{ base: 2, md: "20px" }}
      pl={"20px"}
      // Larger right padding to account for the augmentation toolbar
      // Toolbar is 44px wide and offset by 20px from the right edge
      pr={`calc(20px + 44px + 20px)`}
      zIndex={1}
    >
      {/* Offset the already positioned close button a bit more */}
      <Box pos="absolute" top={{ base: 0, md: 2 }} right={0}>
        <MobileCloseButton />
      </Box>
      <ResourceImageCollapsed
        onClickImage={handleClickImage}
        isActive={true}
        layoutId={"immersive-cover-thumb"}
        fadeInDuration={ANIMATION_DURATION.fade}
        fadeInDelay={0.05}
      />
      <Stack
        flexDirection={"row"}
        justifyContent={{ base: "flex-start", md: "space-between" }}
        alignItems={"center"}
        w="100%"
        spacing={2}
        zIndex={1}
        overflowX="hidden"
      >
        <MotionHeading
          layoutId={"immersive-title"}
          as="h2"
          fontSize={["19px", "22px"]}
          m={0}
          color="white"
          fontWeight="700"
          textShadow="0 1px 5px rgba(0, 0, 0, 0.10)"
          textAlign="left"
          maxW={{ base: "calc(100% - 40px)", md: "100%" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: ANIMATION_DURATION.fade,
            ease: EASE_BEZIER,
            delay: 0.1
          }}
        >
          {title ?? name}
        </MotionHeading>
      </Stack>
    </HStack>
  )
}

export const ImmersiveContent = ({
  children,
  className = ""
}: {
  children: React.ReactNode
  className?: string
}) => {
  const { immersiveRoute } = useCoverPageContext()

  return (
    <CoverPageWrapper
      className={`CoverPageImmersive ${className} ${immersiveRoute?.name}`}
      isFullscreen
      sx={{
        overflow: "hidden",
        bgColor: "transparent"
      }}
    >
      <VStack
        position="relative"
        zIndex={1}
        w="100%"
        h="100%"
        gap={0}
        // Prevent nested card chrome from showing by occupying full viewport
        sx={{
          maxW: "100%",
          maxH: "100%"
        }}
        alignItems="stretch"
        justifyContent="center"
      >
        <ImmersiveHeader />
        <VStack
          className="CoverPageImmersiveContent"
          as="main"
          h="100%"
          w="100%"
          maxH="calc(100vh - 170px)"
          mx="auto"
          overflowY="hidden"
          color="primary"
          // Allow the content to be full width in immersive mode;
          // each route's component should handle its own max width
          px={{ base: 3, sm: 5 }}
          py={COVER_PAGE_PADDING}
          align="stretch"
          justify="center"
        >
          {children}
        </VStack>
        <ImmersiveNavigation />
      </VStack>
    </CoverPageWrapper>
  )
}

const CoverPageImmersiveContainer = ({
  immersiveRoute,
  renderRegisterContent
}: CoverPageContainerProps) => {
  if (!immersiveRoute) return null

  const immersiveRouteConfig = immersiveRoutes.find(
    (r) => r.key === immersiveRoute?.name
  )

  if (!immersiveRouteConfig) return null

  const className = `CoverPage${
    immersiveRouteConfig.key.charAt(0).toUpperCase() +
    immersiveRouteConfig.key.slice(1)
  }`

  const renderArgs = { ...immersiveRoute, renderRegisterContent }

  // A unique key ensures Framer Motion will animate the content
  // Routes with no id are singletons and thus are safe keys
  const identityKey = immersiveRoute.id
    ? `${immersiveRoute.name}-${immersiveRoute.id}`
    : immersiveRoute.name

  const RouteComponent = (
    <Fragment key={`${identityKey}-content`}>
      {immersiveRouteConfig.render(renderArgs)}
    </Fragment>
  )

  return (
    <>
      <MotionBox
        position="absolute"
        inset={0}
        zIndex={1}
        borderRadius="0"
        className="ImmersiveBackground"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: ANIMATION_DURATION.fade, ease: EASE_BEZIER }}
        sx={{
          ".ResourceBackground": {
            borderRadius: "0"
          }
        }}
      >
        <ResourceBackground
          resource={useCoverPageContext((v) => v.resource)!}
          forceBlur
        />
      </MotionBox>
      <ImmersiveContent className={className}>
        <AnimatePresence mode="wait" initial={false}>
          <MotionBox
            className="ImmersiveContentWrapper"
            key={identityKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            py={0}
            maxH="100%"
            overflowY="auto"
            overflowX="hidden"
            mx="auto"
            w="100%"
            maxW={immersiveRouteConfig?.maxWidth ?? "800px"}
            transition={{
              duration: ANIMATION_DURATION.fade,
              ease: EASE_BEZIER
            }}
          >
            {RouteComponent}
          </MotionBox>
        </AnimatePresence>
      </ImmersiveContent>
    </>
  )
}

export default CoverPageImmersiveContainer
